import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import { Route, Router, Switch } from "wouter";

import { useDeploymentFacts } from "./deployment/context.ts";
import { RequireDeploymentFacts } from "./deployment/RequireDeploymentFacts.tsx";
import { NotOpenYet } from "./pages/NotOpenYet.tsx";
import { Home, NotFound, Sign, Verify } from "./pages/placeholders.tsx";

/**
 * The genesis wizard, and through it the account module and viem, load only
 * when `/start` is open: every other page, and `/start` under `onboarding:
 * closed`, ships without them.
 */
const Start = lazy(() =>
  import("./pages/Start.tsx").then(({ Start }) => ({ default: Start })),
);

type AppProps = {
  queryClient: QueryClient;
  /**
   * The path to render at, for a render with no browser — the tests. Absent,
   * the router follows `location`, client-side (ADR-0001: no SSR).
   */
  path?: string;
};

/**
 * The SPA's root: the query client, the router, and the deployment's facts
 * ahead of every route (#161).
 */
export function App({ queryClient, path }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <Router {...(path !== undefined && { ssrPath: path })}>
        <RequireDeploymentFacts>
          <Routes />
        </RequireDeploymentFacts>
      </Router>
    </QueryClientProvider>
  );
}

function Routes() {
  return (
    <Switch>
      <Route path="/">
        <Home />
      </Route>
      <Route path="/start">
        <OnboardingGate>
          <Suspense>
            <Start />
          </Suspense>
        </OnboardingGate>
      </Route>
      <Route path="/sign/:envelope">
        {({ envelope }) => (
          <OnboardingGate>
            <Sign envelope={envelope} />
          </OnboardingGate>
        )}
      </Route>
      <Route path="/verify/:profile">
        {({ profile }) => <Verify profile={profile} />}
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}

/**
 * Wraps every route that can create an identity. Under `onboarding: closed`
 * each one is the same plain page (#158) — whichever route a link named.
 */
function OnboardingGate({ children }: { children: ReactNode }) {
  const { onboarding } = useDeploymentFacts();
  return onboarding === "open" ? children : <NotOpenYet />;
}
